import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatarReais, linkWhatsAppMensagem } from '@/pages/gerenciar/utils';
import type { VendedorRifa } from './types';

interface Props {
  rifaId: number;
  vendedores: VendedorRifa[];
  evento: string | null;
  valorCotaReais: number | null;
}

const MEDALHAS = ['🥇', '🥈', '🥉'];
const TAMANHO_PAGINA = 20;

// "Maria da Graça" → "maria da graca" (sem acento, minúsculas).
function normalizar(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

// Tamanho do talão a partir da faixa ("0001 a 0021" → 21).
function totalDaFaixa(faixa: string | null): number {
  if (!faixa) return 0;
  const nums = faixa.split(/[^0-9]+/).filter(Boolean).map(Number);
  if (nums.length < 2) return 0;
  return nums[nums.length - 1] - nums[0] + 1;
}

export default function VendedoresBloco({ rifaId, vendedores, evento, valorCotaReais }: Props) {
  const [ordem, setOrdem] = useState<'nome' | 'vendidos'>('nome');
  const [busca, setBusca] = useState('');
  const [visiveis, setVisiveis] = useState(TAMANHO_PAGINA);
  const [mostrarSemVendas, setMostrarSemVendas] = useState(false);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    setVisiveis(TAMANHO_PAGINA);
  }, [ordem, busca]);

  const totalVendidos = useMemo(
    () => vendedores.reduce((soma, v) => soma + (v.vendidos || 0), 0),
    [vendedores],
  );
  const ativos = useMemo(
    () => vendedores.filter((v) => (v.vendidos || 0) > 0).length,
    [vendedores],
  );
  const temAlgumaVenda = useMemo(
    () => vendedores.some((v) => (v.vendidos || 0) > 0),
    [vendedores],
  );

  const filtrados = useMemo(() => {
    const termo = normalizar(busca.trim());
    if (!termo) return vendedores;
    return vendedores.filter((v) => normalizar(v.nome).includes(termo));
  }, [vendedores, busca]);

  const ordenados = useMemo(() => {
    const arr = [...filtrados];
    if (ordem === 'vendidos') {
      arr.sort(
        (a, b) => (b.vendidos || 0) - (a.vendidos || 0) || a.nome.localeCompare(b.nome, 'pt-BR'),
      );
    } else {
      arr.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
    }
    return arr;
  }, [filtrados, ordem]);

  const comVendas = useMemo(
    () => ordenados.filter((v) => (v.vendidos || 0) > 0),
    [ordenados],
  );
  const semVendas = useMemo(
    () => ordenados.filter((v) => (v.vendidos || 0) === 0),
    [ordenados],
  );

  const listaTotal = ordem === 'vendidos' ? comVendas : ordenados;
  const listaVisivel = listaTotal.slice(0, visiveis);

  async function copiarRanking() {
    const top = [...vendedores]
      .sort(
        (a, b) => (b.vendidos || 0) - (a.vendidos || 0) || a.nome.localeCompare(b.nome, 'pt-BR'),
      )
      .slice(0, 10);
    const linhas = top
      .map((v, i) => `${i + 1}º ${v.nome.trim().split(' ')[0]} — ${v.vendidos} números`)
      .join('\n');
    const preco =
      valorCotaReais != null
        ? valorCotaReais.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : '—';
    const texto = `🏆 Ranking da ação entre amigos — ${evento || 'nossa campanha'}\n${linhas}\nAinda dá tempo! Cada número R$ ${preco}.`;
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* ignora */
    }
  }

  function renderLinha(v: VendedorRifa, posicao?: number) {
    const total = totalDaFaixa(v.faixa);
    const vendidos = v.vendidos || 0;
    const pct = total > 0 ? Math.min(100, (vendidos / total) * 100) : 0;
    const completo = total > 0 && vendidos >= total;
    const valor = vendidos * (valorCotaReais ?? 0);
    const inicial = (v.nome || '?').charAt(0).toUpperCase();

    return (
      <li key={v.vendedor_id} className="flex items-center gap-3 py-3">
        {ordem === 'vendidos' && (
          <span className="flex w-7 shrink-0 items-center justify-center">
            {posicao != null && (
              <>
                {posicao <= 3 && (
                  <span className="mr-0.5 text-[15px] leading-none">{MEDALHAS[posicao - 1]}</span>
                )}
                <span
                  className={`text-[13px] font-bold tabular-nums ${
                    posicao <= 3 ? 'text-foreground-800' : 'text-foreground-400'
                  }`}
                >
                  {posicao}
                </span>
              </>
            )}
          </span>
        )}

        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[13px] font-bold text-primary-700">
          {inicial}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-[15px] font-semibold text-foreground-900">{v.nome}</p>
            <span className="shrink-0 whitespace-nowrap text-[13px] font-semibold text-foreground-600">
              {vendidos} {vendidos === 1 ? 'número' : 'números'}
            </span>
          </div>
          <p className="mt-0.5 truncate text-[13px] text-foreground-500">
            {v.faixa ? `nº ${v.faixa}` : 'sem talão'} · {formatarReais(valor)}
            {completo ? ' · talão completo' : ''}
          </p>
          <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-background-100">
            <div className="h-1 rounded-full bg-primary-500" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {v.telefone && (
          <a
            href={linkWhatsAppMensagem(
              v.telefone,
              `Se liga católico! 🙏 Compra um número da ação entre amigos e me ajuda a vender:\n${v.link ?? ''}`,
            )}
            target="_blank"
            rel="noreferrer"
            aria-label={`Falar com ${v.nome} no WhatsApp`}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-success-600 transition-colors hover:bg-success-50"
          >
            <i className="ri-whatsapp-line text-xl" />
          </a>
        )}
      </li>
    );
  }

  return (
    <section className="rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      {/* Cabeçalho */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-1">
        <h2 className="text-[17px] font-bold text-foreground-950">Vendedores e talões</h2>
        <div className="flex items-center gap-2">
          {temAlgumaVenda && (
            <button
              type="button"
              onClick={copiarRanking}
              className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-secondary-300 bg-white px-4 text-[14px] font-semibold text-foreground-800 transition-colors hover:bg-background-50"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className={copiado ? 'ri-check-line text-success-600' : 'ri-file-copy-line'} />
              </span>
              {copiado ? 'Copiado' : 'Copiar ranking'}
            </button>
          )}
          <Link
            to={`/sorteio/${rifaId}/imprimir`}
            className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-secondary-300 bg-white px-4 text-[14px] font-semibold text-foreground-800 transition-colors hover:bg-background-50"
          >
            <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
              <i className="ri-printer-line" />
            </span>
            Imprimir talões
          </Link>
        </div>
      </div>

      {/* Controle: por nome / quem mais vendeu */}
      <div className="mt-3 flex rounded-full bg-background-100 p-1">
        <button
          type="button"
          onClick={() => setOrdem('nome')}
          className={`flex-1 cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-[14px] font-semibold transition-colors ${
            ordem === 'nome'
              ? 'bg-white text-primary-700'
              : 'text-foreground-500 hover:text-foreground-700'
          }`}
        >
          Por nome
        </button>
        <button
          type="button"
          onClick={() => setOrdem('vendidos')}
          className={`flex-1 cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-[14px] font-semibold transition-colors ${
            ordem === 'vendidos'
              ? 'bg-white text-primary-700'
              : 'text-foreground-500 hover:text-foreground-700'
          }`}
        >
          Quem mais vendeu
        </button>
      </div>

      {/* Resumo */}
      <div className="mt-3 grid grid-cols-3 gap-2 rounded-2xl bg-background-50 p-3 text-center">
        <div>
          <p className="text-[18px] font-bold tabular-nums text-foreground-950">{totalVendidos}</p>
          <p className="text-[12px] text-foreground-500">números vendidos</p>
        </div>
        <div>
          <p className="text-[18px] font-bold tabular-nums text-foreground-950">{ativos}</p>
          <p className="text-[12px] text-foreground-500">vendedores ativos</p>
        </div>
        <div>
          <p className="text-[18px] font-bold tabular-nums text-foreground-950">
            {vendedores.length}
          </p>
          <p className="text-[12px] text-foreground-500">vendedores</p>
        </div>
      </div>

      {/* Busca */}
      <div className="relative mt-3">
        <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
          <i className="ri-search-line text-sm" />
        </span>
        <input
          type="text"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nome"
          className="w-full rounded-2xl border border-secondary-200 bg-white py-2.5 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
      </div>

      {/* Lista */}
      {vendedores.length === 0 ? (
        <p className="mt-3 px-1 text-[14px] text-foreground-500">
          Nenhum vendedor ainda. Reparta os talões para vender.
        </p>
      ) : listaVisivel.length === 0 ? (
        <p className="mt-3 px-1 text-[14px] text-foreground-500">Nenhum vendedor encontrado.</p>
      ) : (
        <>
          <ul className="mt-2 divide-y divide-background-100">
            {listaVisivel.map((v, i) => renderLinha(v, ordem === 'vendidos' ? i + 1 : undefined))}
          </ul>
          {listaTotal.length > visiveis && (
            <button
              type="button"
              onClick={() => setVisiveis((n) => n + TAMANHO_PAGINA)}
              className="mt-3 w-full cursor-pointer whitespace-nowrap rounded-2xl border border-secondary-300 bg-white py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-50"
            >
              Ver mais ({listaTotal.length - visiveis})
            </button>
          )}
        </>
      )}

      {/* Quem ainda não vendeu (só no ranking) */}
      {ordem === 'vendidos' && semVendas.length > 0 && (
        <div className="mt-3 rounded-2xl bg-background-50 p-2">
          <button
            type="button"
            onClick={() => setMostrarSemVendas((v) => !v)}
            className="flex w-full cursor-pointer items-center justify-between rounded-xl px-3 py-2.5 text-[14px] font-medium text-foreground-600 transition-colors hover:bg-background-100"
          >
            <span>Ainda não venderam ({semVendas.length})</span>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={mostrarSemVendas ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
            </span>
          </button>
          {mostrarSemVendas && (
            <ul className="mt-1 divide-y divide-background-100">
              {semVendas.map((v) => renderLinha(v))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}